"use client";

import React from "react";
import { Camera, Sparkles, Bell } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  id: "step-1",
  title: "Start an Order",
  description: "Launch a new food run or join a live order from Toronto's top restaurants in your immediate area.",
  icon: Camera
}, {
  id: "step-2",
  title: "Smart Matching",
  description: "Our real-time proximity engine pairs you with nearby users ordering from the same spot to maximize savings.",
  icon: Sparkles
}, {
  id: "step-3",
  title: "Secure Splitting",
  description: "Pay only your share via escrow. We handle the math so everyone pays fairly before the food arrives.",
  icon: Bell
}];
export default function TheSolutionFlow() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="180560" id="detailed-step-by-step" className="py-24 dark bg-background text-foreground relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-primary/10 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {}
        <div ref={headerRef} className={`text-center max-w-2xl mx-auto mb-20 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Badge variant="outline" className="mb-4 border-primary/30 bg-primary/10 text-primary uppercase tracking-widest font-mono text-xs">
            How It Works
          </Badge>
          <h2 className="text-4xl md:text-6xl font-black tracking-tighter text-foreground mb-6 uppercase">
            The Social Split
          </h2>
          <p className="text-lg text-muted-foreground font-medium">
            A high-speed, secure process designed for the Toronto lifestyle to help you save money on every single meal.
          </p>
        </div>

        {}
        <div ref={gridRef} className={`relative transition-all duration-1000 delay-200 ease-out ${isGridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {}
          <div className="absolute top-24 md:top-1/2 left-8 md:left-[10%] w-[2px] md:w-[80%] h-[calc(100%-6rem)] md:h-[2px] -translate-x-1/2 md:-translate-x-0 md:-translate-y-1/2 bg-primary/20 z-0 overflow-hidden rounded-full">
            {}
            <div className="w-full h-1/3 md:w-1/3 md:h-full bg-gradient-to-b md:bg-gradient-to-r from-transparent via-primary/80 to-transparent animate-pulse" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8 relative z-10">
            {steps.map((step, i) => <div key={step.id} data-index={i} className="relative group">
                <Card className="h-full bg-card/40 backdrop-blur-2xl border-white/10 shadow-xl rounded-sm transition-all duration-300 hover:shadow-primary/20 hover:-translate-y-2 overflow-hidden">
                  
                  {}
                  <div className="absolute inset-0 rounded-sm ring-1 ring-inset ring-white/5 pointer-events-none" />

                  <CardHeader className="text-center pb-2 pt-10">
                    <div className="mx-auto mb-6 relative">
                      {}
                      <div className="absolute inset-0 bg-primary/20 rounded-full scale-150 blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      <div className="relative w-20 h-20 mx-auto bg-card border border-white/10 shadow-lg rounded-sm flex items-center justify-center rotate-2 group-hover:rotate-0 transition-transform duration-300">
                        <step.icon className="size-10 text-primary" strokeWidth={2} />
                      </div>
                    </div>
                    
                    <Badge className="mx-auto mb-4 bg-primary/20 text-primary hover:bg-primary/30 border-none shadow-none font-black tracking-widest uppercase text-[10px] px-3 py-1 rounded-none">
                      Step 0{i + 1}
                    </Badge>
                    
                    <CardTitle className="text-2xl font-black text-foreground uppercase tracking-tight">
                      {step.title}
                    </CardTitle>
                  </CardHeader>
                  
                  <CardContent className="text-center px-8 pb-10">
                    <CardDescription className="text-base text-muted-foreground leading-relaxed font-medium">
                      {step.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </div>)}
          </div>
        </div>

      </div>
    </section>;
}